import React, { useEffect, useMemo, useState } from 'react';
import Swal from '../../Librarian/librarianSwal';
import { useAuth } from '../../Constant/Auth';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import FilterSelect from '../../Librarian/components/FilterSelect';
import SearchableSelect from '../../shared/SearchableSelect';
import {
  getEmployeeListAdmin,
  getRolesList,
  getAllLibrariesAdmin,
  updateUserStatusAdmin,
  registerUser,
  sendMobileOtp,
  verifyMobileOtp,
  getErrorMessage
} from '../../api/api';
import '../../Librarian/lib-page-header.css';
import '../../Librarian/BookList.css';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  mobile_no: '',
  role_id: '',
  library_id: '',
  aadhaar_img: null,
  user_img: null,
  status: '1'
};

const UserList = () => {
  const { user } = useAuth();
  // School Admin (role 2) is locked to their own school; Super Admin sees all.
  const scopeLibraryId = user?.role_id === 2 ? user?.library_id : null;

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [libraries, setLibraries] = useState([]);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // OTP (optional mobile verification)
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [emps, r, libs] = await Promise.all([
        getEmployeeListAdmin(),
        getRolesList().catch(() => []),
        getAllLibrariesAdmin().catch(() => [])
      ]);
      setUsers(Array.isArray(emps) ? emps : []);
      setRoles(Array.isArray(r) ? r : []);
      setLibraries(Array.isArray(libs) ? libs : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const roleName = (id) => {
    const r = roles.find((x) => String(x.id) === String(id));
    return r ? r.name || r.role_name || `Role #${id}` : `Role #${id}`;
  };
  const libName = (id) => {
    const l = libraries.find((x) => String(x.id) === String(id));
    return l ? l.library_name || `#${id}` : (id ? `#${id}` : '—');
  };

  // When locked to one school, resolve its name for the header and hide the
  // (now redundant) Library column.
  const schoolName = scopeLibraryId
    ? libraries.find((l) => String(l.id) === String(scopeLibraryId))?.library_name
    : '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (scopeLibraryId && String(u.library_id) !== String(scopeLibraryId)) return false;
      if (roleFilter && String(u.role_id) !== String(roleFilter)) return false;
      if (!q) return true;
      const hay = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''} ${u.mobile_no || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [users, search, roleFilter, scopeLibraryId]);

  const toggleStatus = async (u) => {
    const currentlyActive = Number(u.status) === 1;
    const res = await Swal.fire({
      title: currentlyActive ? 'Deactivate user?' : 'Activate user?',
      text: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: currentlyActive ? 'Deactivate' : 'Activate',
      confirmButtonColor: currentlyActive ? '#dc2626' : '#16a34a'
    });
    if (!res.isConfirmed) return;
    try {
      const newStatus = currentlyActive ? 0 : 1;
      await updateUserStatusAdmin(u.id, newStatus);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status: newStatus } : x)));
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const openAdd = () => {
    // School Admin can only create users inside their own school.
    setForm({ ...EMPTY_FORM, library_id: scopeLibraryId ? String(scopeLibraryId) : '' });
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setModalOpen(true);
  };

  const handleSendOtp = async () => {
    if (!form.mobile_no) {
      Swal.fire('Mobile required', 'Enter a mobile number first.', 'warning');
      return;
    }
    try {
      await sendMobileOtp(form.mobile_no);
      setOtpSent(true);
      Swal.fire('OTP sent', 'An OTP has been sent to the mobile number.', 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return;
    try {
      const r = await verifyMobileOtp(form.mobile_no, otp);
      if (r?.success) {
        setOtpVerified(true);
        Swal.fire('Verified', 'Mobile number verified.', 'success');
      } else {
        Swal.fire('Invalid OTP', r?.message || 'Verification failed.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.role_id) {
      Swal.fire('Missing fields', 'First name, email and role are required.', 'warning');
      return;
    }
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, v);
      });
      fd.set('status', '1');
      await registerUser(fd);
      Swal.fire('User created', 'The new user has been added.', 'success');
      setModalOpen(false);
      fetchAll();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-users-cog" /></div>
          <div>
            <h3 className="lib-page-header__title">Users &amp; Librarians</h3>
            <div className="lib-page-header__subtitle">
              {scopeLibraryId
                ? `Manage staff accounts for ${schoolName || 'your school'}`
                : 'Manage staff accounts across all libraries'}
            </div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="book-add-btn" onClick={openAdd}>
            <i className="fas fa-user-plus" /> Add User
          </button>
        </div>
      </div>

      <div className="book-filter-card">
        <div className="book-filter-row">
          <div className="search__input" style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              className="book-search-input"
              placeholder="Search by name, email or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <FilterSelect
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="All roles"
            options={[{ value: '', label: 'All roles' }, ...roles.map((r) => ({ value: String(r.id), label: r.name || r.role_name }))]}
          />
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <div className="book-table-card">
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sr.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  {!scopeLibraryId && <th>Library</th>}
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={scopeLibraryId ? 7 : 8} className="book-empty">No users found</td>
                  </tr>
                ) : (
                  filtered.map((u, i) => {
                    const active = Number(u.status) === 1;
                    return (
                      <tr key={u.id}>
                        <td>{i + 1}</td>
                        <td><strong>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</strong></td>
                        <td>{u.email || '—'}</td>
                        <td>{u.mobile_no || '—'}</td>
                        <td><span className="book-count book-count-blue">{roleName(u.role_id)}</span></td>
                        {!scopeLibraryId && <td>{libName(u.library_id)}</td>}
                        <td>
                          <span className={`book-status ${active ? 'book-badge-success' : 'book-badge-secondary'}`}>
                            {active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="book-action-buttons">
                            <button
                              className={`book-action-btn ${active ? 'delete' : 'status'}`}
                              onClick={() => toggleStatus(u)}
                            >
                              {active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="cat-modal-overlay" onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="cat-modal">
            <div className="cat-modal-header">
              <div>
                <h4>Add User</h4>
                <p>Create a new staff account</p>
              </div>
              <button className="cat-modal-close" onClick={() => setModalOpen(false)}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="cat-form">
              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>First Name <span>*</span></label>
                  <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div className="cat-form-group">
                  <label>Last Name</label>
                  <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
              </div>

              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Email <span>*</span></label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="cat-form-group">
                  <label>Password</label>
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              </div>

              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Mobile Number</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      style={{ flex: 1 }}
                      value={form.mobile_no}
                      onChange={(e) => { setForm({ ...form, mobile_no: e.target.value }); setOtpVerified(false); setOtpSent(false); }}
                    />
                    <button type="button" className="book-cat-manage-btn" onClick={handleSendOtp}>
                      {otpSent ? 'Resend' : 'Send OTP'}
                    </button>
                  </div>
                </div>
                {otpSent && !otpVerified && (
                  <div className="cat-form-group">
                    <label>Enter OTP</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input style={{ flex: 1 }} value={otp} onChange={(e) => setOtp(e.target.value)} />
                      <button type="button" className="book-cat-manage-btn" onClick={handleVerifyOtp}>Verify</button>
                    </div>
                  </div>
                )}
                {otpVerified && (
                  <div className="cat-form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>
                      <i className="fas fa-check-circle" /> Mobile verified
                    </span>
                  </div>
                )}
              </div>

              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Role <span>*</span></label>
                  <SearchableSelect
                    value={form.role_id}
                    onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                    options={roles.map((r) => ({ value: r.id, label: r.name || r.role_name }))}
                    placeholder="Select role"
                  />
                </div>
                <div className="cat-form-group">
                  <label>Library {scopeLibraryId && <span style={{ color: '#94a3b8' }}>(your school)</span>}</label>
                  <SearchableSelect
                    value={form.library_id}
                    onChange={(e) => setForm({ ...form, library_id: e.target.value })}
                    disabled={!!scopeLibraryId}
                    options={libraries.map((l) => ({ value: l.id, label: l.library_name }))}
                    placeholder="Select library"
                  />
                </div>
              </div>

              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Aadhaar Image</label>
                  <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, aadhaar_img: e.target.files[0] || null })} />
                </div>
                <div className="cat-form-group">
                  <label>Profile Image</label>
                  <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, user_img: e.target.files[0] || null })} />
                </div>
              </div>

              <div className="cat-form-actions">
                <button className="cat-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Creating...' : 'Create User'}
                </button>
                <button className="cat-btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
